import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import {
  GALLERY_CONSTS,
  ProGallery,
  type CreateMediaUrl,
  type CustomComponents,
} from '@wix/pro-gallery';
import { type GalleryProps } from '@wix/pro-gallery-lib';
import { useEnvironment, useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import {
  isLayoutSlider,
  isLayoutTextOnImage,
  isLayoutPGSideBySide,
  isLayoutPGGrid,
  PgLayoutImageCropTypeOptions,
  resolveId,
  type LayoutName,
  type Section,
  type LayoutType,
  type Layout,
} from '@wix/communities-blog-client-common';
import { OOI_EXPERIMENTS } from '@app/experiments';
import type { PostAction } from '../../constants/post-actions';
import { isClickTargetItemMedia, isPGEventItemClicked } from '../../constants/pro-gallery-events';
import { HOVERING_BEHAVIOUR, TEXT_PLACEMENT } from '../../constants/pro-gallery-options';
import { useFeedBorderWidth } from '../../hooks/use-feed-border-width';
import { useIsFeedDesignEnabled } from '../../hooks/use-is-feed-design-enabled';
import {
  getContainer,
  getPGEmptyCoverImage,
  getScrollingElement,
  getWrapperStyles,
} from '../../services/post-list-pro-gallery';
import { type NormalizedPost } from '../../types';
import { useActions } from '../runtime-context';
import { createInfoRenderer } from './create-info-renderer';
import { createOptions } from './gallery-options';
import { mapPostToPGItem, getPGFallbackImageSize } from './map-post-to-pro-gallery-item';
import { useSlice, type LayoutDefaults, type PickedLayoutOptions } from './post-list-pro-gallery';
import { PostListProGalleryLayoutFixer } from './post-list-pro-gallery-layout-fixer';
import { resizeMediaUrl } from './resizeMediaUrl';
import { useScrollBase } from './use-scroll-base';
import styles from './post-list-pro-gallery.scss';

/** Renders items as <article> instead of <div>. These props aren't public */
const internalGalleryProps = {
  elementsOverride: { item: 'article' },
} as const;

type Props = {
  allPosts: NormalizedPost[];
  layoutName?: LayoutName;
  section: Section;
  layoutType?: Layout | LayoutType;
  layoutDefaults?: LayoutDefaults;
  domId?: string;
  hideCoverImage?: boolean;
  layoutOptions?: PickedLayoutOptions;
  canSeeMoreButton: (post: NormalizedPost) => boolean;
  visibleActions?: PostAction[];
  className?: string;
};

export const PostListProGalleryComponent = ({
  section,
  layoutName,
  layoutType,
  layoutDefaults,
  domId,
  allPosts,
  hideCoverImage,
  layoutOptions,
  canSeeMoreButton,
  visibleActions,
  className,
}: Props) => {
  const { t } = useTranslation();
  const scrollingElementRef = useRef(getScrollingElement());
  const actions = useActions();
  const { isEditor, isRTL } = useEnvironment();
  const { experiments } = useExperiments();
  const { borderWidth } = useFeedBorderWidth();
  const { getPostClassName } = useIsFeedDesignEnabled();
  const useLayoutFixerExperimentEnabled = experiments.enabled(OOI_EXPERIMENTS.USE_LAYOUT_FIXER);

  const slice = useSlice({
    section,
    layoutType,
    layoutName,
    layoutDefaults,
  });

  const container = getContainer({
    borderWidth,
    isEditor,
    useLayoutFixerExperimentEnabled,
    createdWithResponsiveEditor: slice.createdWithResponsiveEditor,
    hostWidth: slice.hostWidth,
    isMobile: slice.isMobile,
    layoutContentHeight: slice.layoutContentHeight,
    layoutImageRatio: slice.layoutImageRatio,
    layoutMargins: slice.layoutMargins,
    layoutPostSize: slice.layoutPostSize,
    layoutType: slice.layoutType,
    rootWidth: slice.rootWidth,
    section: slice.section!,
    useMobilePostListLayout: slice.useMobilePostListLayout,
  });

  const wrapperStyles = getWrapperStyles({
    container,
    createdWithResponsiveEditor: slice.createdWithResponsiveEditor,
    isMobile: slice.isMobile,
    layoutMargins: slice.layoutMargins,
    layoutPostSize: slice.layoutPostSize,
    layoutType: slice.layoutType,
    section: slice.section!,
  });

  const containerRef = React.useRef(null);
  const scrollBase = useScrollBase(containerRef);
  // Key on primitives (not `container` by reference) because `getContainer` returns
  // a fresh object every render. Without this, the object identity changes every
  // render regardless of value changes and defeats downstream memoisation.
  const galleryContainer = useMemo(
    () => ({ width: container.width, height: container.height, scrollBase }),
    [container.width, container.height, scrollBase],
  );
  const htmlId = domId ?? 'pro-blog';
  const deviceType = slice.isMobile
    ? GALLERY_CONSTS.deviceType.MOBILE
    : GALLERY_CONSTS.deviceType.DESKTOP;

  const videoSettingsPlayMode = slice.videoSettings.play;
  const eventsListener = useCallback(
    (eventName: string, eventData: any) => {
      if (
        !isLayoutTextOnImage(slice.layoutType) &&
        isPGEventItemClicked(eventName) &&
        isClickTargetItemMedia(eventData)
      ) {
        const currentPost = allPosts.find((post) => resolveId(post) === eventData.id);

        if (
          !currentPost?.slug ||
          (currentPost.coverImage?.videoMetadata && videoSettingsPlayMode === 'onClick')
        ) {
          return;
        }

        actions.navigateProGalleryWithinPostPage(currentPost.slug);
      }
    },
    [allPosts, slice.layoutType, videoSettingsPlayMode, actions],
  );

  const options = useMemo(
    () =>
      createOptions(
        slice.layoutType,
        {
          isRTL,
          containerWidth: getContainer({
            borderWidth,
            isEditor,
            useLayoutFixerExperimentEnabled,
            layoutType: slice.layoutType,
            section: slice.section!,
            createdWithResponsiveEditor: slice.createdWithResponsiveEditor,
            hostWidth: slice.hostWidth,
            isMobile: slice.isMobile,
            layoutContentHeight: slice.layoutContentHeight,
            layoutImageRatio: slice.layoutImageRatio,
            layoutMargins: slice.layoutMargins,
            layoutPostSize: slice.layoutPostSize,
            rootWidth: slice.rootWidth,
            useMobilePostListLayout: slice.useMobilePostListLayout,
          }).width,
          hideCoverImage,
          layoutAutoHeightEnabled: slice.layoutAutoHeightEnabled,
          layoutCardsRowType: slice.layoutCardsRowType,
          layoutContentHeight: slice.layoutContentHeight,
          layoutImageCropType: slice.layoutImageCropType,
          layoutImageProportions: slice.layoutImageProportions,
          layoutImageRatio: slice.layoutImageRatio,
          layoutImageResizingMode: slice.layoutImageResizingMode,
          layoutImageWidth: slice.layoutImageWidth,
          layoutPostSize: isLayoutSlider(slice.layoutType) ? slice.hostWidth : slice.layoutPostSize,
          layoutPostsPerRow: slice.layoutPostsPerRow,
          layoutSpacing: slice.layoutSpacing,
          textBoxAlignment: slice.textBoxAlignment,
          videoSettings: slice.videoSettings,
          ...layoutOptions,
        },
        {
          width: borderWidth,
          color: slice.borderColor,
        },
      ),
    [
      borderWidth,
      isEditor,
      isRTL,
      useLayoutFixerExperimentEnabled,
      hideCoverImage,
      layoutOptions,
      slice.borderColor,
      slice.createdWithResponsiveEditor,
      slice.hostWidth,
      slice.isMobile,
      slice.layoutAutoHeightEnabled,
      slice.layoutCardsRowType,
      slice.layoutContentHeight,
      slice.layoutImageCropType,
      slice.layoutImageProportions,
      slice.layoutImageRatio,
      slice.layoutImageResizingMode,
      slice.layoutImageWidth,
      slice.layoutMargins,
      slice.layoutPostSize,
      slice.layoutPostsPerRow,
      slice.layoutSpacing,
      slice.layoutType,
      slice.rootWidth,
      slice.section,
      slice.textBoxAlignment,
      slice.useMobilePostListLayout,
      slice.videoSettings,
    ],
  );

  const infoRenderer = useMemo(
    () =>
      createInfoRenderer({
        getPostClassName,
        options,
        prevAllPosts: [],
        allPosts,
        canSeeMoreButton,
        layoutName: slice.layoutName,
        layoutType: slice.layoutType,
        section: slice.section!,
        visibleActions,
        layoutSidesPadding: slice.layoutSidesPadding,
        textBoxAlignment: slice.textBoxAlignment,
      }),
    [
      options,
      getPostClassName,
      allPosts,
      canSeeMoreButton,
      visibleActions,
      slice.layoutName,
      slice.layoutType,
      slice.section,
      slice.textBoxAlignment,
      slice.layoutSidesPadding,
    ],
  );

  const hoveringBehavior = 'hoveringBehaviour' in options ? options.hoveringBehaviour : undefined;
  const customComponents = useMemo(() => {
    if (!infoRenderer) {
      return undefined;
    }

    return {
      customHoverRenderer:
        hoveringBehavior === HOVERING_BEHAVIOUR.NEVER_SHOW ? undefined : infoRenderer,
      customInfoRenderer: infoRenderer,
    };
  }, [infoRenderer, hoveringBehavior]);

  const items = useMemo(() => {
    const fallbackSize = getPGFallbackImageSize(options, slice.layoutPostSize);
    const isFitMode = `${slice.layoutImageCropType}` === PgLayoutImageCropTypeOptions.Max;
    const isSideBySideFitMode = isLayoutPGSideBySide(slice.layoutType) && isFitMode;
    const isGridFitMode = isLayoutPGGrid(slice.layoutType) && isFitMode;

    const emptyCoverImage =
      isSideBySideFitMode || isGridFitMode
        ? { ...getPGEmptyCoverImage(slice.layoutType), ...fallbackSize }
        : getPGEmptyCoverImage(slice.layoutType);

    return allPosts.map((post) =>
      mapPostToPGItem(post, hideCoverImage, emptyCoverImage, fallbackSize),
    );
  }, [
    slice.layoutType,
    slice.layoutPostSize,
    slice.layoutImageCropType,
    options,
    allPosts,
    hideCoverImage,
  ]);

  const noBorder = options.stylingParams_itemBorderWidth === 0;

  return (
    <div
      key="pg-container"
      data-hook="post-list-pro-gallery-container"
      style={wrapperStyles}
      className={classNames(
        `post-list-pro-gallery-${slice.layoutName}`,
        { 'is-mobile': slice.isMobile, 'no-border': noBorder },
        slice.section,
        slice.isMobile && items.length === 1 ? 'post-list-pro-gallery-flat' : '',
        slice.textBoxAlignment === TEXT_PLACEMENT.SHOW_ON_THE_RIGHT
          ? 'gallery-left-align'
          : 'gallery-right-align',
        styles.overrides,
        className,
      )}
      ref={containerRef}
    >
      <PostListProGalleryLayoutFixer id={htmlId} items={items} options={options}>
        {(isPrerenderMode) => (
          <ProGallery
            {...internalGalleryProps}
            id={htmlId}
            items={items}
            totalItemsCount={items.length}
            options={options as GalleryProps['options']}
            container={galleryContainer}
            scrollingElement={scrollingElementRef.current}
            eventsListener={eventsListener}
            viewMode={slice.viewMode}
            deviceType={deviceType}
            customComponents={customComponents as CustomComponents}
            createMediaUrl={resizeMediaUrl as CreateMediaUrl}
            proGalleryRegionLabel={t('pro-gallery.parent-container-aria-label')}
            isPrerenderMode={isPrerenderMode}
          />
        )}
      </PostListProGalleryLayoutFixer>
    </div>
  );
};
